import styled from '@emotion/styled'
import { onFormatDate } from 'components'
import {
  Status1Icon,
  Status2Icon,
  Status3Icon,
} from 'components/CustomIcons/StatusIcons'
import {
  ColumnRenderer,
  EntityList,
  EntityListProps,
  renderDate,
  renderEnum,
  renderMandatory,
  renderPersona,
  renderYesNoIcon,
} from 'components/EntityList'
import { Stack } from 'forms/FormStack'
import { Box } from 'grid'
import { TFunction } from 'i18next'
import {
  BookingStatus,
  CarType,
  FlightType,
  Participant,
  PersonType,
} from 'model'
import { IGroup, PersonaSize, SelectionMode } from 'office-ui-fabric-react'
import * as React from 'react'
import { useTranslation } from 'react-i18next'
import { createFieldColumn } from 'views/helpers'
import { ParticipantListItem } from './BookingByStatusView'

const StyledBox = styled(Box)`
  padding-top: 3px;
`
const renderFerry: (t: TFunction) => ColumnRenderer =
  (t: TFunction) => (item: Participant, i, props) => {
    if (item.ferry && item.ferry.outward && item.ferry.return) {
      const content = `${t('field.ferryConnection.outward.label')}: ${
        item.ferry.outward.title
      }\n${t('field.ferryConnection.return.label')}: ${item.ferry.return.title}`
      return <span style={{ whiteSpace: 'pre-line' }}>{content}</span>
    } else if (item.ferry && item.ferry.outward) {
      const content = `${t('field.ferryConnection.outward.label')}: ${
        item.ferry.outward.title
      }`
      return <span style={{ whiteSpace: 'pre-line' }}>{content}</span>
    } else if (item.ferry && item.ferry.return) {
      const content = `${t('field.ferryConnection.return.label')}: ${
        item.ferry.return.title
      }`
      return <span style={{ whiteSpace: 'pre-line' }}>{content}</span>
    } else {
      return ''
    }
  }

export type ParticipantViewListProps = EntityListProps<ParticipantListItem> & {
  groups: IGroup[]
}

export const BookingByStatusListNestedGrouping: React.FC<
  ParticipantViewListProps
> = ({ items, groups, ...props }) => {
  const { t } = useTranslation()

  const fieldColumn = createFieldColumn(t)
  const columns = [
    fieldColumn('person', {
      isSorted: false,
      onRender: (item: Participant, i, col) => {
        if (
          item.unknownPerson &&
          item.unknownPerson.isUnknown &&
          item.unknownPerson.personType === 'user'
        ) {
          return t('model.proving.participant.participantUnknown')
        } else if (
          item.unknownPerson &&
          item.unknownPerson.isUnknown &&
          item.unknownPerson.personType === 'worker'
        ) {
          return item.percentage + '%, ' + item.unknownPerson.department
        } else if (
          item.unknownPerson &&
          item.unknownPerson.isUnknown &&
          item.unknownPerson.personType === 'supplier'
        ) {
          return item.unknownPerson.supplier
            ? `${item.unknownPerson.surName}, ${item.unknownPerson.firstName} - ${item.unknownPerson.supplier}`
            : '[Teilnehmer noch unbekannt]'
        } else if (
          item.unknownPerson &&
          !item.unknownPerson.isUnknown &&
          item.unknownPerson.personType === 'worker'
        ) {
          return (
            <Stack horizontal={true}>
              <Stack.Item>
                {renderPersona({
                  size: PersonaSize.size24,
                })(item, i, col)}
              </Stack.Item>
              <Stack.Item>
                <StyledBox>{item.percentage}% </StyledBox>
              </Stack.Item>
            </Stack>
          )
        } else {
          return renderPersona({
            size: PersonaSize.size24,
          })(item, i, col)
        }
      },
      minWidth: 155,
    }),
    fieldColumn('dateFrom', {
      isSorted: false,
      onRender: renderDate,
    }),
    fieldColumn('dateTo', {
      isSorted: false,
      onRender: renderDate,
    }),
    fieldColumn('orderId', {
      isSorted: false,
      onRender: (item: Participant, i, col) => {
        if (
          (!item.orderId &&
            item.flight &&
            item.flight.flightType !== FlightType.NoFlight) ||
          (!item.orderId &&
            item.rentalCar &&
            item.rentalCar.carType !== CarType.None) ||
          (!item.orderId &&
            item.person &&
            item.person.type === PersonType.Worker) ||
          (!item.orderId &&
            item.unknownPerson &&
            item.unknownPerson.personType === PersonType.Worker)
        ) {
          return renderMandatory(item, i, col)
        } else {
          return item.orderId
        }
      },
    }),
    fieldColumn('flight.flightType', {
      isSorted: false,
      onRender: (item: Participant, i, col) => {
        switch (item.flight!.bookingStatus) {
          case 'open':
            return (
              <Box>
                <Status1Icon />{' '}
                {renderEnum(t, 'flight.flightType')(item, i, col)}
              </Box>
            )
          case 'workInProgress':
            return (
              <Box>
                <Status2Icon />{' '}
                {renderEnum(t, 'flight.flightType')(item, i, col)}
              </Box>
            )
          case 'finished':
            return (
              <Box>
                <Status3Icon />{' '}
                {renderEnum(t, 'flight.flightType')(item, i, col)}
              </Box>
            )
          default:
            return renderEnum(t, 'flight.flightType')(item, i, col)
        }
      },
    }),
    fieldColumn('accomodation.accomodationType', {
      isSorted: false,
      onRender: (item: Participant, i, col) => {
        switch (item.accomodation!.bookingStatus) {
          case 'open':
            return (
              <Box>
                <Status1Icon />{' '}
                {item &&
                item.accomodation &&
                item.accomodation.accomodation &&
                item.accomodation.accomodation.title
                  ? item.accomodation.accomodation.title
                  : renderEnum(t, 'accomodationType')(item, i, col)}
              </Box>
            )
          case 'workInProgress':
            return (
              <Box>
                <Status2Icon />{' '}
                {item &&
                item.accomodation &&
                item.accomodation.accomodation &&
                item.accomodation.accomodation.title
                  ? item.accomodation.accomodation.title
                  : renderEnum(t, 'accomodationType')(item, i, col)}
              </Box>
            )
          case 'finished':
            return (
              <Box>
                <Status3Icon />{' '}
                {item &&
                item.accomodation &&
                item.accomodation.accomodation &&
                item.accomodation.accomodation.title
                  ? item.accomodation.accomodation.title
                  : renderEnum(t, 'accomodationType')(item, i, col)}
              </Box>
            )
          default:
            return item &&
              item.accomodation &&
              item.accomodation.accomodation &&
              item.accomodation.accomodation.title
              ? item.accomodation.accomodation.title
              : renderEnum(t, 'accomodationType')(item, i, col)
        }
      },
      minWidth: 150,
    }),
    fieldColumn('rentalCar.carType', {
      isSorted: false,
      onRender: (item: Participant, i, col) => {
        switch (item.rentalCar!.bookingStatus) {
          case 'open':
            return (
              <Box>
                <Status1Icon /> {renderEnum(t, 'rentalCar')(item, i, col)}
              </Box>
            )
          case 'workInProgress':
            return (
              <Box>
                <Status2Icon /> {renderEnum(t, 'rentalCar')(item, i, col)}
              </Box>
            )
          case 'finished':
            return (
              <Box>
                <Status3Icon /> {renderEnum(t, 'rentalCar')(item, i, col)}
              </Box>
            )
          default:
            return renderEnum(t, 'rentalCar')(item, i, col)
        }
      },
    }),
    fieldColumn('nordschleife', {
      isSorted: false,
      onRender: renderYesNoIcon,
    }),
    fieldColumn('ferry', {
      isSorted: false,
      onRender: (item: Participant, i, col) => {
        switch (item.ferry!.bookingStatus) {
          case 'open':
            return (
              <Box>
                <Status1Icon /> {renderFerry(t)(item, i, col)}
              </Box>
            )
          case 'workInProgress':
            return (
              <Box>
                <Status2Icon /> {renderFerry(t)(item, i, col)}
              </Box>
            )
          case 'finished':
            return (
              <Box>
                <Status3Icon /> {renderFerry(t)(item, i, col)}
              </Box>
            )
          default:
            return renderFerry(t)(item, i, col)
        }
      },
      minWidth: 175,
    }),
    fieldColumn('trackHotels.outward[0]', {
      useFullFieldname: true,
      minWidth: 155,
      ungroupable: true,
      onRender: (item: Participant, i, col) => {
        if (
          item.trackHotels &&
          item.trackHotels.outward &&
          item.trackHotels.outward[0] &&
          item.trackHotels.outward[0].hotel
        ) {
          const trackHotel = item.trackHotels.outward[0]
          return (
            <Box style={{ whiteSpace: 'pre-line' }}>
              {trackHotel.bookingStatus === BookingStatus.Open ? (
                <Status1Icon />
              ) : trackHotel.bookingStatus === BookingStatus.WorkInProgress ? (
                <Status2Icon />
              ) : trackHotel.bookingStatus === BookingStatus.Finished ? (
                <Status3Icon />
              ) : (
                <></>
              )}{' '}
              {trackHotel.hotel!.title!}{' '}
              {onFormatDate(new Date(trackHotel!.dateFrom))} -{' '}
              {onFormatDate(new Date(trackHotel!.dateTo))}
            </Box>
          )
        } else {
          return <></>
        }
      },
    }),
    fieldColumn('trackHotels.outward[1]', {
      useFullFieldname: true,
      minWidth: 155,
      ungroupable: true,
      onRender: (item: Participant, i, col) => {
        if (
          item.trackHotels &&
          item.trackHotels.outward &&
          item.trackHotels.outward[1] &&
          item.trackHotels.outward[1].hotel
        ) {
          const trackHotel = item.trackHotels.outward[1]
          return (
            <Box style={{ whiteSpace: 'pre-line' }}>
              {trackHotel.bookingStatus === BookingStatus.Open ? (
                <Status1Icon />
              ) : trackHotel.bookingStatus === BookingStatus.WorkInProgress ? (
                <Status2Icon />
              ) : trackHotel.bookingStatus === BookingStatus.Finished ? (
                <Status3Icon />
              ) : (
                <></>
              )}{' '}
              {trackHotel.hotel!.title!}{' '}
              {onFormatDate(new Date(trackHotel!.dateFrom))} -{' '}
              {onFormatDate(new Date(trackHotel!.dateTo))}
            </Box>
          )
        } else {
          return <></>
        }
      },
    }),
    fieldColumn('trackHotels.outward[2]', {
      useFullFieldname: true,
      minWidth: 155,
      ungroupable: true,
      onRender: (item: Participant, i, col) => {
        if (
          item.trackHotels &&
          item.trackHotels.outward &&
          item.trackHotels.outward[2] &&
          item.trackHotels.outward[2].hotel
        ) {
          const trackHotel = item.trackHotels.outward[2]
          return (
            <Box style={{ whiteSpace: 'pre-line' }}>
              {trackHotel.bookingStatus === BookingStatus.Open ? (
                <Status1Icon />
              ) : trackHotel.bookingStatus === BookingStatus.WorkInProgress ? (
                <Status2Icon />
              ) : trackHotel.bookingStatus === BookingStatus.Finished ? (
                <Status3Icon />
              ) : (
                <></>
              )}{' '}
              {trackHotel.hotel!.title!}{' '}
              {onFormatDate(new Date(trackHotel!.dateFrom))} -{' '}
              {onFormatDate(new Date(trackHotel!.dateTo))}
            </Box>
          )
        } else {
          return <></>
        }
      },
    }),
    fieldColumn('trackHotels.outward[3]', {
      useFullFieldname: true,
      minWidth: 155,
      ungroupable: true,
      onRender: (item: Participant, i, col) => {
        if (
          item.trackHotels &&
          item.trackHotels.outward &&
          item.trackHotels.outward[3] &&
          item.trackHotels.outward[3].hotel
        ) {
          const trackHotel = item.trackHotels.outward[3]
          return (
            <Box style={{ whiteSpace: 'pre-line' }}>
              {trackHotel.bookingStatus === BookingStatus.Open ? (
                <Status1Icon />
              ) : trackHotel.bookingStatus === BookingStatus.WorkInProgress ? (
                <Status2Icon />
              ) : trackHotel.bookingStatus === BookingStatus.Finished ? (
                <Status3Icon />
              ) : (
                <></>
              )}{' '}
              {trackHotel.hotel!.title!}{' '}
              {onFormatDate(new Date(trackHotel!.dateFrom))} -{' '}
              {onFormatDate(new Date(trackHotel!.dateTo))}
            </Box>
          )
        } else {
          return <></>
        }
      },
    }),
    fieldColumn('trackHotels.outward[4]', {
      useFullFieldname: true,
      minWidth: 155,
      ungroupable: true,
      onRender: (item: Participant, i, col) => {
        if (
          item.trackHotels &&
          item.trackHotels.outward &&
          item.trackHotels.outward[4] &&
          item.trackHotels.outward[4].hotel
        ) {
          const trackHotel = item.trackHotels.outward[4]
          return (
            <Box style={{ whiteSpace: 'pre-line' }}>
              {trackHotel.bookingStatus === BookingStatus.Open ? (
                <Status1Icon />
              ) : trackHotel.bookingStatus === BookingStatus.WorkInProgress ? (
                <Status2Icon />
              ) : trackHotel.bookingStatus === BookingStatus.Finished ? (
                <Status3Icon />
              ) : (
                <></>
              )}{' '}
              {trackHotel.hotel!.title!}{' '}
              {onFormatDate(new Date(trackHotel!.dateFrom))} -{' '}
              {onFormatDate(new Date(trackHotel!.dateTo))}
            </Box>
          )
        } else {
          return <></>
        }
      },
    }),
    fieldColumn('trackHotels.outward[5]', {
      useFullFieldname: true,
      minWidth: 155,
      ungroupable: true,
      onRender: (item: Participant, i, col) => {
        if (
          item.trackHotels &&
          item.trackHotels.outward &&
          item.trackHotels.outward[5] &&
          item.trackHotels.outward[5].hotel
        ) {
          const trackHotel = item.trackHotels.outward[5]
          return (
            <Box style={{ whiteSpace: 'pre-line' }}>
              {trackHotel.bookingStatus === BookingStatus.Open ? (
                <Status1Icon />
              ) : trackHotel.bookingStatus === BookingStatus.WorkInProgress ? (
                <Status2Icon />
              ) : trackHotel.bookingStatus === BookingStatus.Finished ? (
                <Status3Icon />
              ) : (
                <></>
              )}{' '}
              {trackHotel.hotel!.title!}{' '}
              {onFormatDate(new Date(trackHotel!.dateFrom))} -{' '}
              {onFormatDate(new Date(trackHotel!.dateTo))}
            </Box>
          )
        } else {
          return <></>
        }
      },
    }),
    fieldColumn('trackHotels.outward[6]', {
      useFullFieldname: true,
      minWidth: 155,
      ungroupable: true,
      onRender: (item: Participant, i, col) => {
        if (
          item.trackHotels &&
          item.trackHotels.outward &&
          item.trackHotels.outward[6] &&
          item.trackHotels.outward[6].hotel
        ) {
          const trackHotel = item.trackHotels.outward[6]
          return (
            <Box style={{ whiteSpace: 'pre-line' }}>
              {trackHotel.bookingStatus === BookingStatus.Open ? (
                <Status1Icon />
              ) : trackHotel.bookingStatus === BookingStatus.WorkInProgress ? (
                <Status2Icon />
              ) : trackHotel.bookingStatus === BookingStatus.Finished ? (
                <Status3Icon />
              ) : (
                <></>
              )}{' '}
              {trackHotel.hotel!.title!}{' '}
              {onFormatDate(new Date(trackHotel!.dateFrom))} -{' '}
              {onFormatDate(new Date(trackHotel!.dateTo))}
            </Box>
          )
        } else {
          return <></>
        }
      },
    }),
    fieldColumn('trackHotels.outward[7]', {
      useFullFieldname: true,
      minWidth: 155,
      ungroupable: true,
      onRender: (item: Participant, i, col) => {
        if (
          item.trackHotels &&
          item.trackHotels.outward &&
          item.trackHotels.outward[7] &&
          item.trackHotels.outward[7].hotel
        ) {
          const trackHotel = item.trackHotels.outward[7]
          return (
            <Box style={{ whiteSpace: 'pre-line' }}>
              {trackHotel.bookingStatus === BookingStatus.Open ? (
                <Status1Icon />
              ) : trackHotel.bookingStatus === BookingStatus.WorkInProgress ? (
                <Status2Icon />
              ) : trackHotel.bookingStatus === BookingStatus.Finished ? (
                <Status3Icon />
              ) : (
                <></>
              )}{' '}
              {trackHotel.hotel!.title!}{' '}
              {onFormatDate(new Date(trackHotel!.dateFrom))} -{' '}
              {onFormatDate(new Date(trackHotel!.dateTo))}
            </Box>
          )
        } else {
          return <></>
        }
      },
    }),
    fieldColumn('trackHotels.outward[8]', {
      useFullFieldname: true,
      minWidth: 155,
      ungroupable: true,
      onRender: (item: Participant, i, col) => {
        if (
          item.trackHotels &&
          item.trackHotels.outward &&
          item.trackHotels.outward[8] &&
          item.trackHotels.outward[8].hotel
        ) {
          const trackHotel = item.trackHotels.outward[8]
          return (
            <Box style={{ whiteSpace: 'pre-line' }}>
              {trackHotel.bookingStatus === BookingStatus.Open ? (
                <Status1Icon />
              ) : trackHotel.bookingStatus === BookingStatus.WorkInProgress ? (
                <Status2Icon />
              ) : trackHotel.bookingStatus === BookingStatus.Finished ? (
                <Status3Icon />
              ) : (
                <></>
              )}{' '}
              {trackHotel.hotel!.title!}{' '}
              {onFormatDate(new Date(trackHotel!.dateFrom))} -{' '}
              {onFormatDate(new Date(trackHotel!.dateTo))}
            </Box>
          )
        } else {
          return <></>
        }
      },
    }),
    fieldColumn('trackHotels.outward[9]', {
      useFullFieldname: true,
      minWidth: 155,
      ungroupable: true,
      onRender: (item: Participant, i, col) => {
        if (
          item.trackHotels &&
          item.trackHotels.outward &&
          item.trackHotels.outward[9] &&
          item.trackHotels.outward[9].hotel
        ) {
          const trackHotel = item.trackHotels.outward[9]
          return (
            <Box style={{ whiteSpace: 'pre-line' }}>
              {trackHotel.bookingStatus === BookingStatus.Open ? (
                <Status1Icon />
              ) : trackHotel.bookingStatus === BookingStatus.WorkInProgress ? (
                <Status2Icon />
              ) : trackHotel.bookingStatus === BookingStatus.Finished ? (
                <Status3Icon />
              ) : (
                <></>
              )}{' '}
              {trackHotel.hotel!.title!}{' '}
              {onFormatDate(new Date(trackHotel!.dateFrom))} -{' '}
              {onFormatDate(new Date(trackHotel!.dateTo))}
            </Box>
          )
        } else {
          return <></>
        }
      },
    }),
    fieldColumn('trackHotels.return[0]', {
      useFullFieldname: true,
      minWidth: 155,
      ungroupable: true,
      onRender: (item: Participant, i, col) => {
        if (
          item.trackHotels &&
          item.trackHotels.return &&
          item.trackHotels.return[0] &&
          item.trackHotels.return[0].hotel
        ) {
          const trackHotel = item.trackHotels.return[0]
          return (
            <Box style={{ whiteSpace: 'pre-line' }}>
              {trackHotel.bookingStatus === BookingStatus.Open ? (
                <Status1Icon />
              ) : trackHotel.bookingStatus === BookingStatus.WorkInProgress ? (
                <Status2Icon />
              ) : trackHotel.bookingStatus === BookingStatus.Finished ? (
                <Status3Icon />
              ) : (
                <></>
              )}{' '}
              {trackHotel.hotel!.title!}{' '}
              {onFormatDate(new Date(trackHotel!.dateFrom))} -{' '}
              {onFormatDate(new Date(trackHotel!.dateTo))}
            </Box>
          )
        } else {
          return <></>
        }
      },
    }),
    fieldColumn('trackHotels.return[1]', {
      useFullFieldname: true,
      minWidth: 155,
      ungroupable: true,
      onRender: (item: Participant, i, col) => {
        if (
          item.trackHotels &&
          item.trackHotels.return &&
          item.trackHotels.return[1] &&
          item.trackHotels.return[1].hotel
        ) {
          const trackHotel = item.trackHotels.return[1]
          return (
            <Box style={{ whiteSpace: 'pre-line' }}>
              {trackHotel.bookingStatus === BookingStatus.Open ? (
                <Status1Icon />
              ) : trackHotel.bookingStatus === BookingStatus.WorkInProgress ? (
                <Status2Icon />
              ) : trackHotel.bookingStatus === BookingStatus.Finished ? (
                <Status3Icon />
              ) : (
                <></>
              )}{' '}
              {trackHotel.hotel!.title!}{' '}
              {onFormatDate(new Date(trackHotel!.dateFrom))} -{' '}
              {onFormatDate(new Date(trackHotel!.dateTo))}
            </Box>
          )
        } else {
          return <></>
        }
      },
    }),
    fieldColumn('trackHotels.return[2]', {
      useFullFieldname: true,
      minWidth: 155,
      ungroupable: true,
      onRender: (item: Participant, i, col) => {
        if (
          item.trackHotels &&
          item.trackHotels.return &&
          item.trackHotels.return[2] &&
          item.trackHotels.return[2].hotel
        ) {
          const trackHotel = item.trackHotels.return[2]
          return (
            <Box style={{ whiteSpace: 'pre-line' }}>
              {trackHotel.bookingStatus === BookingStatus.Open ? (
                <Status1Icon />
              ) : trackHotel.bookingStatus === BookingStatus.WorkInProgress ? (
                <Status2Icon />
              ) : trackHotel.bookingStatus === BookingStatus.Finished ? (
                <Status3Icon />
              ) : (
                <></>
              )}{' '}
              {trackHotel.hotel!.title!}{' '}
              {onFormatDate(new Date(trackHotel!.dateFrom))} -{' '}
              {onFormatDate(new Date(trackHotel!.dateTo))}
            </Box>
          )
        } else {
          return <></>
        }
      },
    }),
    fieldColumn('trackHotels.return[3]', {
      useFullFieldname: true,
      minWidth: 155,
      ungroupable: true,
      onRender: (item: Participant, i, col) => {
        if (
          item.trackHotels &&
          item.trackHotels.return &&
          item.trackHotels.return[3] &&
          item.trackHotels.return[3].hotel
        ) {
          const trackHotel = item.trackHotels.return[3]
          return (
            <Box style={{ whiteSpace: 'pre-line' }}>
              {trackHotel.bookingStatus === BookingStatus.Open ? (
                <Status1Icon />
              ) : trackHotel.bookingStatus === BookingStatus.WorkInProgress ? (
                <Status2Icon />
              ) : trackHotel.bookingStatus === BookingStatus.Finished ? (
                <Status3Icon />
              ) : (
                <></>
              )}{' '}
              {trackHotel.hotel!.title!}{' '}
              {onFormatDate(new Date(trackHotel!.dateFrom))} -{' '}
              {onFormatDate(new Date(trackHotel!.dateTo))}
            </Box>
          )
        } else {
          return <></>
        }
      },
    }),
    fieldColumn('trackHotels.return[4]', {
      useFullFieldname: true,
      minWidth: 155,
      ungroupable: true,
      onRender: (item: Participant, i, col) => {
        if (
          item.trackHotels &&
          item.trackHotels.return &&
          item.trackHotels.return[4] &&
          item.trackHotels.return[4].hotel
        ) {
          const trackHotel = item.trackHotels.return[4]
          return (
            <Box style={{ whiteSpace: 'pre-line' }}>
              {trackHotel.bookingStatus === BookingStatus.Open ? (
                <Status1Icon />
              ) : trackHotel.bookingStatus === BookingStatus.WorkInProgress ? (
                <Status2Icon />
              ) : trackHotel.bookingStatus === BookingStatus.Finished ? (
                <Status3Icon />
              ) : (
                <></>
              )}{' '}
              {trackHotel.hotel!.title!}{' '}
              {onFormatDate(new Date(trackHotel!.dateFrom))} -{' '}
              {onFormatDate(new Date(trackHotel!.dateTo))}
            </Box>
          )
        } else {
          return <></>
        }
      },
    }),
    fieldColumn('trackHotels.return[5]', {
      useFullFieldname: true,
      minWidth: 155,
      ungroupable: true,
      onRender: (item: Participant, i, col) => {
        if (
          item.trackHotels &&
          item.trackHotels.return &&
          item.trackHotels.return[5] &&
          item.trackHotels.return[5].hotel
        ) {
          const trackHotel = item.trackHotels.return[5]
          return (
            <Box style={{ whiteSpace: 'pre-line' }}>
              {trackHotel.bookingStatus === BookingStatus.Open ? (
                <Status1Icon />
              ) : trackHotel.bookingStatus === BookingStatus.WorkInProgress ? (
                <Status2Icon />
              ) : trackHotel.bookingStatus === BookingStatus.Finished ? (
                <Status3Icon />
              ) : (
                <></>
              )}{' '}
              {trackHotel.hotel!.title!}{' '}
              {onFormatDate(new Date(trackHotel!.dateFrom))} -{' '}
              {onFormatDate(new Date(trackHotel!.dateTo))}
            </Box>
          )
        } else {
          return <></>
        }
      },
    }),
    fieldColumn('trackHotels.return[6]', {
      useFullFieldname: true,
      minWidth: 155,
      ungroupable: true,
      onRender: (item: Participant, i, col) => {
        if (
          item.trackHotels &&
          item.trackHotels.return &&
          item.trackHotels.return[6] &&
          item.trackHotels.return[6].hotel
        ) {
          const trackHotel = item.trackHotels.return[6]
          return (
            <Box style={{ whiteSpace: 'pre-line' }}>
              {trackHotel.bookingStatus === BookingStatus.Open ? (
                <Status1Icon />
              ) : trackHotel.bookingStatus === BookingStatus.WorkInProgress ? (
                <Status2Icon />
              ) : trackHotel.bookingStatus === BookingStatus.Finished ? (
                <Status3Icon />
              ) : (
                <></>
              )}{' '}
              {trackHotel.hotel!.title!}{' '}
              {onFormatDate(new Date(trackHotel!.dateFrom))} -{' '}
              {onFormatDate(new Date(trackHotel!.dateTo))}
            </Box>
          )
        } else {
          return <></>
        }
      },
    }),
    fieldColumn('trackHotels.return[7]', {
      useFullFieldname: true,
      minWidth: 155,
      ungroupable: true,
      onRender: (item: Participant, i, col) => {
        if (
          item.trackHotels &&
          item.trackHotels.return &&
          item.trackHotels.return[7] &&
          item.trackHotels.return[7].hotel
        ) {
          const trackHotel = item.trackHotels.return[7]
          return (
            <Box style={{ whiteSpace: 'pre-line' }}>
              {trackHotel.bookingStatus === BookingStatus.Open ? (
                <Status1Icon />
              ) : trackHotel.bookingStatus === BookingStatus.WorkInProgress ? (
                <Status2Icon />
              ) : trackHotel.bookingStatus === BookingStatus.Finished ? (
                <Status3Icon />
              ) : (
                <></>
              )}{' '}
              {trackHotel.hotel!.title!}{' '}
              {onFormatDate(new Date(trackHotel!.dateFrom))} -{' '}
              {onFormatDate(new Date(trackHotel!.dateTo))}
            </Box>
          )
        } else {
          return <></>
        }
      },
    }),
    fieldColumn('trackHotels.return[8]', {
      useFullFieldname: true,
      minWidth: 155,
      ungroupable: true,
      onRender: (item: Participant, i, col) => {
        if (
          item.trackHotels &&
          item.trackHotels.return &&
          item.trackHotels.return[8] &&
          item.trackHotels.return[8].hotel
        ) {
          const trackHotel = item.trackHotels.return[8]
          return (
            <Box style={{ whiteSpace: 'pre-line' }}>
              {trackHotel.bookingStatus === BookingStatus.Open ? (
                <Status1Icon />
              ) : trackHotel.bookingStatus === BookingStatus.WorkInProgress ? (
                <Status2Icon />
              ) : trackHotel.bookingStatus === BookingStatus.Finished ? (
                <Status3Icon />
              ) : (
                <></>
              )}{' '}
              {trackHotel.hotel!.title!}{' '}
              {onFormatDate(new Date(trackHotel!.dateFrom))} -{' '}
              {onFormatDate(new Date(trackHotel!.dateTo))}
            </Box>
          )
        } else {
          return <></>
        }
      },
    }),
    fieldColumn('trackHotels.return[9]', {
      useFullFieldname: true,
      minWidth: 155,
      ungroupable: true,
      onRender: (item: Participant, i, col) => {
        if (
          item.trackHotels &&
          item.trackHotels.return &&
          item.trackHotels.return[9] &&
          item.trackHotels.return[9].hotel
        ) {
          const trackHotel = item.trackHotels.return[9]
          return (
            <Box style={{ whiteSpace: 'pre-line' }}>
              {trackHotel.bookingStatus === BookingStatus.Open ? (
                <Status1Icon />
              ) : trackHotel.bookingStatus === BookingStatus.WorkInProgress ? (
                <Status2Icon />
              ) : trackHotel.bookingStatus === BookingStatus.Finished ? (
                <Status3Icon />
              ) : (
                <></>
              )}{' '}
              {trackHotel.hotel!.title!}{' '}
              {onFormatDate(new Date(trackHotel!.dateFrom))} -{' '}
              {onFormatDate(new Date(trackHotel!.dateTo))}
            </Box>
          )
        } else {
          return <></>
        }
      },
    }),
  ]

  return (
    <EntityList
      items={items}
      groups={groups}
      stickyHeader={true}
      columns={columns}
      alternate={true}
      selectionMode={SelectionMode.multiple}
      {...props}
    />
  )
}
